import { useForm } from '@mantine/form';
import {
  Stack,
  Box,
  Group,
  Text,
  Button,
  Grid,
  SimpleGrid,
  TextInput,
  NumberInput,
  Select,
  useMantineTheme,
  Divider,
} from '@mantine/core';
import {
  IconArrowRight,
  IconUpload,
  IconX,
  IconCloudUpload,
} from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';
import { Dropzone, DropzoneProps, PDF_MIME_TYPE } from '@mantine/dropzone';
import { notifyError } from '../../utils/NotifyError.tsx';

import {
  PROFILE_TYPE_LABEL,
  RECEIPT_MODE_LABEL,
  CHAIN_STORE_CONTRACT_PROFIT_SHARING_TYPE_LABEL,
  CHAIN_STORE_CONTRACT_PROFIT_SHARING_UNIT,
  labelMapToOptionList,
} from '../../const/labels.ts';

import type { ProfileType, ReceiptMode } from '../../models/contract.ts';
import type { ProfitSharingMethod } from '../../models/chain-store-contract.ts';
import type { Attachment, NewAttachment } from '../../models/attachment.ts';

export interface MutateChainStoreContractFormValues {
  name: string;
  profileType: ProfileType;
  profitSharingMethod: ProfitSharingMethod;
  profitSharingValue: string;
  startAt: string;
  endAt: string;
  chainStoreNotificationEndpoint: string;
  requestAmountLimit: number;
  receiptMode: ReceiptMode;
  displayAttachments: (Attachment | NewAttachment)[];
  existingAttachments: Attachment[];
  newAttachments: NewAttachment[];
  deletedAttachments: Attachment[];
}

export type MutateChainStoreContractFormInitialValues = Omit<
  MutateChainStoreContractFormValues,
  'newAttachments' | 'deletedAttachments' | 'displayAttachments'
>;

const profileTypeSelections = labelMapToOptionList(PROFILE_TYPE_LABEL);
const profitSharingSelections = labelMapToOptionList(
  CHAIN_STORE_CONTRACT_PROFIT_SHARING_TYPE_LABEL,
).map(({ label, value }) => ({
  label: `${label}（${CHAIN_STORE_CONTRACT_PROFIT_SHARING_UNIT[value]}）`,
  value,
}));
const receiptModeSelections = labelMapToOptionList(RECEIPT_MODE_LABEL);

interface MutateChainStoreContractFormProps {
  initialValues: MutateChainStoreContractFormInitialValues;
  onSubmit: (formValues: MutateChainStoreContractFormValues) => void;
}

export default function MutateChainStoreContractForm({
  initialValues,
  onSubmit,
}: MutateChainStoreContractFormProps) {
  const dropZoneProps: Partial<DropzoneProps> = {
    accept: PDF_MIME_TYPE,
    maxSize: 30000000, // 30MB
  };
  const theme = useMantineTheme();

  const form = useForm<MutateChainStoreContractFormValues>({
    mode: 'controlled',
    initialValues: {
      displayAttachments: initialValues.existingAttachments,
      newAttachments: [],
      deletedAttachments: [],
      ...initialValues,
    },
    validate: {
      name: (value) => (value ? null : ' '),
      startAt: (value, values) =>
        value && values.endAt && new Date(value) <= new Date(values.endAt)
          ? null
          : ' ',
      endAt: (value, values) =>
        value && values.startAt && new Date(values.startAt) <= new Date(value)
          ? null
          : ' ',
      profitSharingValue: (value) =>
        value && 0 <= parseFloat(value) && parseFloat(value) <= 100
          ? null
          : ' ',
      newAttachments: (value, values) => {
        if (value.length > 1) {
          return '只能上傳一份檔案';
        }
        if (
          values.existingAttachments.length +
            value.length -
            values.deletedAttachments.length <
          1
        ) {
          return '附件不可為空';
        }
        return null;
      },
    },
  });

  function onClickDeleteFile(onDeleteAttachment: Attachment | NewAttachment) {
    form.setValues((prev) => ({
      ...prev,
      newAttachments: prev.newAttachments.filter(
        (attachment) => attachment !== onDeleteAttachment,
      ),
      displayAttachments: prev.displayAttachments.filter(
        (attachment) => attachment !== onDeleteAttachment,
      ),
      deletedAttachments: attachmentIsExisting(onDeleteAttachment)
        ? [...prev.deletedAttachments, onDeleteAttachment]
        : prev.deletedAttachments,
    }));
  }

  function onDrop(files) {
    const attachments = files.map((file) => {
      return {
        displayName: file.name,
        file: file,
      };
    });
    form.setValues((prev) => ({
      ...prev,
      newAttachments: [...prev.newAttachments, ...attachments],
      displayAttachments: [...prev.displayAttachments, ...attachments],
    }));
  }

  function attachmentIsExisting(
    attachment: Attachment | NewAttachment,
  ): attachment is Attachment {
    return 'id' in attachment;
  }

  return (
    <form
      id="mutateChainStoreContractForm"
      onSubmit={form.onSubmit(
        (values: MutateChainStoreContractFormValues) => onSubmit(values),
        () => {
          notifyError('合約儲存失敗', '尚有欄位未填寫完成或格式錯誤');
        },
      )}
    >
      {/* 基本設定 */}
      <Text size="sm" fw={500} c="gray" mt={40}>
        基本設定
      </Text>
      <Divider mt={10} mb={20} />
      <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
        <TextInput
          withAsterisk
          label="合約名稱"
          placeholder="請輸入合約名稱"
          key={form.key('name')}
          {...form.getInputProps('name')}
        ></TextInput>
        <Select
          allowDeselect={false}
          label="合約套用方式"
          data={profileTypeSelections}
          defaultValue="DEFAULT"
          key={form.key('profileType')}
          {...form.getInputProps('profileType')}
        ></Select>
        <Group>
          <DatePickerInput
            withAsterisk
            label="合約生效起迄日"
            placeholder="請輸入合約生效起始日"
            style={{ flex: 1 }}
            valueFormat="YYYY/MM/DD"
            key={form.key('startAt')}
            {...form.getInputProps('startAt')}
          ></DatePickerInput>
          <Stack>
            <Box h={12}></Box> {/* 塞個東東讓下面的箭頭位置正確 */}
            <IconArrowRight />
          </Stack>
          <DatePickerInput
            label=" "
            placeholder="請輸入合約生效結束日"
            style={{ flex: 1 }}
            valueFormat="YYYY/MM/DD"
            key={form.key('endAt')}
            {...form.getInputProps('endAt')}
          ></DatePickerInput>
        </Group>
        <TextInput
          label="連鎖店交易通知網址"
          placeholder="請輸入連鎖店交易通知網址"
          key={form.key('chainStoreNotificationEndpoint')}
          {...form.getInputProps('chainStoreNotificationEndpoint')}
        ></TextInput>
        <Select
          allowDeselect={false}
          label="發票開立方式"
          data={receiptModeSelections}
          defaultValue="DEFAULT"
          key={form.key('receiptMode')}
          {...form.getInputProps('receiptMode')}
        />
        <TextInput
          label="請款金額上限"
          readOnly
          disabled
          value={initialValues.requestAmountLimit}
        ></TextInput>
      </SimpleGrid>

      {/* 分潤方式 */}
      <Text size="sm" fw={500} c="gray" mt={60}>
        分潤方式
      </Text>
      <Divider mt={10} mb={20} />
      <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
        <Grid columns={4}>
          <Grid.Col span={3}>
            <Select
              withAsterisk
              allowDeselect={false}
              label="分潤條件"
              data={profitSharingSelections}
              style={{ flex: 1 }}
              key={form.key('profitSharingMethod')}
              {...form.getInputProps('profitSharingMethod')}
            ></Select>
          </Grid.Col>
          <Grid.Col span={1}>
            <NumberInput
              label=" "
              placeholder="0 ~ 100"
              min={0}
              max={100}
              decimalScale={2}
              key={form.key('profitSharingValue')}
              {...form.getInputProps('profitSharingValue')}
            ></NumberInput>
          </Grid.Col>
        </Grid>
      </SimpleGrid>

      {/* 合約上傳 */}
      <Text size="sm" fw={500} c="gray" mt={60} mb={20}>
        合約上傳
      </Text>
      {form.values.displayAttachments.length === 0 ? (
        <>
          <Dropzone
            onDrop={(files) => onDrop(files)}
            onReject={() => alert('只可上傳 < 30MB 的 PDF 檔案！')}
            maxSize={5 * 1024 ** 2}
            accept={PDF_MIME_TYPE}
            {...dropZoneProps}
            p="xl"
            style={{
              border: form.errors.newAttachments
                ? '2px dashed var(--mantine-color-red-6 )'
                : '2px dashed var(--mantine-color-gray-2 )',
            }}
          >
            <Group
              justify="center"
              gap="xl"
              mih={220}
              style={{ cursor: 'pointer' }}
            >
              <Dropzone.Accept>
                <IconUpload
                  size={52}
                  color={theme.colors.blue[6]}
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={52} color={theme.colors.red[6]} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconCloudUpload size={52} color="#212529" stroke={1.5} />
              </Dropzone.Idle>

              <Box>
                <Text size="xl" inline>
                  上傳連鎖店紙本合約
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  把檔案拖曳到這裡上傳。只接受小於 30MB 的 .pdf 檔案。
                </Text>
              </Box>
            </Group>
          </Dropzone>
        </>
      ) : (
        <>
          <Text size="sm" fw={500} inline mb={10}>
            已選擇檔案：
          </Text>
          {form.values.displayAttachments.map((attachment) => {
            return (
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  {attachment.displayName}
                </Text>
                <Button
                  variant="transparent"
                  color="red"
                  onClick={() => onClickDeleteFile(attachment)}
                >
                  <Text size="sm" fw={500} td="underline">
                    刪除檔案
                  </Text>
                </Button>
              </Group>
            );
          })}
        </>
      )}

      {form.errors.newAttachments && (
        <Text c="red" fz="xs" mt={4}>
          {form.errors.newAttachments}
        </Text>
      )}
    </form>
  );
}
